import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import * as adminService from '../../services/admin';

const PendingApprovals = () => {
    const [pendingApprovals, setpendingApprovals] = useState([]);
    const [allRequests, setAllRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [filter, setFilter] = useState({
        status: '',
        module: ''
    });

    const currentUser = JSON.parse(localStorage.getItem('userdetails'));

    useEffect(() => {
        loadPendingApprovals();
        loadAllRequests();
    }, []);

    const loadPendingApprovals = async () => {
        try {
            setLoading(true);
            const response = await adminService.listPendingApprovals(currentUser.id);
            if (response.status === 'success') {
                setpendingApprovals(response.data);
            }
        } catch (error) {
            console.error('Error loading pending approvals:', error);
            toast.error('Failed to load pending approvals');
        } finally {
            setLoading(false);
        }
    };

    const loadAllRequests = async () => {
        try {
            const response = await adminService.listApprovalRequests({});
            if (response.status === 'success') {
                setAllRequests(response.data);
            }
        } catch (error) {
            console.error('Error loading all requests:', error);
        }
    };

    const loadRequestDetails = async (requestId) => {
        try {
            const response = await adminService.getApprovalRequestDetails(requestId);
            if (response.status === 'success') {
                setSelectedRequest(response.data);
                setShowDetailsModal(true);
            }
        } catch (error) {
            console.error('Error loading request details:', error);
            toast.error('Failed to load request details');
        }
    };

    const handleApprove = async (requestId) => {
        const result = await Swal.fire({
            title: 'Approve Request',
            input: 'textarea',
            inputLabel: 'Comments (optional)',
            inputPlaceholder: 'Enter your comments...',
            showCancelButton: true,
            confirmButtonText: 'Approve',
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminService.processApproval(requestId, {
                    action: 'approved',
                    comments: result.value,
                    approver_id: currentUser.id
                });

                if (response.status === 'success') {
                    toast.success('Request approved successfully');
                    loadPendingApprovals();
                    loadAllRequests();
                    setShowDetailsModal(false);
                } else {
                    toast.error(response.message || 'Failed to approve request');
                }
            } catch (error) {
                console.error('Error approving request:', error);
                toast.error('Failed to approve request');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleReject = async (requestId) => {
        const result = await Swal.fire({
            title: 'Reject Request',
            input: 'textarea',
            inputLabel: 'Reason for rejection *',
            inputPlaceholder: 'Please provide a reason for rejection...',
            inputValidator: (value) => {
                if (!value) {
                    return 'Reason is required for rejection';
                }
            },
            showCancelButton: true,
            confirmButtonText: 'Reject',
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminService.processApproval(requestId, {
                    action: 'rejected',
                    comments: result.value,
                    approver_id: currentUser.id
                });

                if (response.status === 'success') {
                    toast.success('Request rejected successfully');
                    loadPendingApprovals();
                    loadAllRequests();
                    setShowDetailsModal(false);
                } else {
                    toast.error(response.message || 'Failed to reject request');
                }
            } catch (error) {
                console.error('Error rejecting request:', error);
                toast.error('Failed to reject request');
            } finally {
                setLoading(false);
            }
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'pending': 'bg-warning',
            'approved': 'bg-success',
            'rejected': 'bg-danger'
        };
        return badges[status] || 'bg-secondary';
    };

    const filteredRequests = allRequests.filter(req => {
        if (filter.status && req.status !== filter.status) return false;
        if (filter.module && req.workflow.module !== filter.module) return false;
        return true;
    });

    return (
        <div className="container-fluid mt-3">
            <div className="row">
                {/* Pending Approvals Card */}
                <div className="col-md-12 mb-4">
                    <div className="card">
                        <div className="card-header bg-warning text-dark">
                            <h5 className="mb-0">
                                <i className="fa fa-clock"></i> My Pending Approvals
                                <span className="badge bg-danger ms-2">{pendingApprovals.length}</span>
                            </h5>
                        </div>
                        <div className="card-body">
                            {pendingApprovals.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Request ID</th>
                                                <th>Module</th>
                                                <th>Workflow</th>
                                                <th>Requested By</th>
                                                <th>Current Level</th>
                                                <th>Requested At</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingApprovals.map(request => (
                                                <tr key={request.id}>
                                                    <td>#{request.id}</td>
                                                    <td>
                                                        <span className="badge bg-info text-capitalize">
                                                            {request.workflow.module}
                                                        </span>
                                                    </td>
                                                    <td>{request.workflow.workflow_name}</td>
                                                    <td>{request.requester.name}</td>
                                                    <td>
                                                        Level {request.current_level} of {request.workflow.approval_levels}
                                                    </td>
                                                    <td>{moment(request.requested_at).format('DD-MMM-YYYY HH:mm')}</td>
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-sm btn-info me-2"
                                                            onClick={() => loadRequestDetails(request.id)}
                                                        >
                                                            <i className="fa fa-eye"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-success me-2"
                                                            onClick={() => handleApprove(request.id)}
                                                            disabled={loading}
                                                        >
                                                            <i className="fa fa-check"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleReject(request.id)}
                                                            disabled={loading}
                                                        >
                                                            <i className="fa fa-times"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center text-muted py-4">
                                    <i className="fa fa-check-circle fa-3x mb-3"></i>
                                    <p>No pending approvals</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* All Approval Requests */}
                <div className="col-md-12">
                    <div className="card">
                        <div className="card-header bg-primary text-white">
                            <h5 className="mb-0">
                                <i className="fa fa-list"></i> All Approval Requests
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <select
                                                className="default-select  form-control wide"
                                        value={filter.status}
                                        onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="pending">Pending</option>
                                        <option value="approved">Approved</option>
                                        <option value="rejected">Rejected</option>
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <select
                                                className="default-select  form-control wide"
                                        value={filter.module}
                                        onChange={(e) => setFilter({ ...filter, module: e.target.value })}
                                    >
                                        <option value="">All Modules</option>
                                        <option value="vouchers">Vouchers</option>
                                        <option value="budgets">Budgets</option>
                                        <option value="fixed_assets">Fixed Assets</option>
                                        <option value="loans">Loans</option>
                                    </select>
                                </div>
                            </div>

                            {filteredRequests.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Request ID</th>
                                                <th>Module</th>
                                                <th>Workflow</th>
                                                <th>Requested By</th>
                                                <th>Status</th>
                                                <th>Level</th>
                                                <th>Requested At</th>
                                                <th>Completed At</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredRequests.map(request => (
                                                <tr key={request.id}>
                                                    <td>#{request.id}</td>
                                                    <td>
                                                        <span className="badge bg-info text-capitalize">
                                                            {request.workflow.module}
                                                        </span>
                                                    </td>
                                                    <td>{request.workflow.workflow_name}</td>
                                                    <td>{request.requester.name}</td>
                                                    <td>
                                                        <span className={`badge ${getStatusBadge(request.status)} text-capitalize`}>
                                                            {request.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {request.current_level} / {request.workflow.approval_levels}
                                                    </td>
                                                    <td>{moment(request.requested_at).format('DD-MMM-YYYY HH:mm')}</td>
                                                    <td>
                                                        {request.completed_at
                                                            ? moment(request.completed_at).format('DD-MMM-YYYY HH:mm')
                                                            : '-'}
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-sm btn-info"
                                                            onClick={() => loadRequestDetails(request.id)}
                                                        >
                                                            <i className="fa fa-eye"></i> View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center text-muted py-4">
                                    <p>No approval requests found</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Request Details Modal */}
            {showDetailsModal && selectedRequest && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Approval Request Details - #{selectedRequest.id}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowDetailsModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <strong>Workflow:</strong> {selectedRequest.workflow.workflow_name}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Module:</strong>{' '}
                                        <span className="badge bg-info text-capitalize">
                                            {selectedRequest.workflow.module}
                                        </span>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <strong>Requested By:</strong> {selectedRequest.requester.name}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Status:</strong>{' '}
                                        <span className={`badge ${getStatusBadge(selectedRequest.status)} text-capitalize`}>
                                            {selectedRequest.status}
                                        </span>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-12">
                                        <strong>Remarks:</strong>
                                        <p className="mb-0">{selectedRequest.remarks || 'N/A'}</p>
                                    </div>
                                </div>

                                <hr />

                                <h6 className="mb-3">Approval History</h6>
                                {selectedRequest.actions && selectedRequest.actions.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Level</th>
                                                    <th>Approver</th>
                                                    <th>Action</th>
                                                    <th>Comments</th>
                                                    <th>Action At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedRequest.actions.map(action => (
                                                    <tr key={action.id}>
                                                        <td>Level {action.level}</td>
                                                        <td>{action.approver.name}</td>
                                                        <td>
                                                            <span className={`badge ${getStatusBadge(action.action)} text-capitalize`}>
                                                                {action.action}
                                                            </span>
                                                        </td>
                                                        <td>{action.comments || '-'}</td>
                                                        <td>{moment(action.action_at).format('DD-MMM-YYYY HH:mm')}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-muted">No actions yet</p>
                                )}
                            </div>
                            <div className="modal-footer">
                                {selectedRequest.status === 'pending' &&
                                    pendingApprovals.some(req => req.id === selectedRequest.id) && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-success"
                                                onClick={() => handleApprove(selectedRequest.id)}
                                                disabled={loading}
                                            >
                                                <i className="fa fa-check"></i> Approve
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={() => handleReject(selectedRequest.id)}
                                                disabled={loading}
                                            >
                                                <i className="fa fa-times"></i> Reject
                                            </button>
                                        </>
                                    )}
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowDetailsModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PendingApprovals;
